import {
  faChevronLeft,
  faChevronRight,
  faIdCard,
  faLocationDot,
  faUser,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import CartDrawer from "src/Components/AdminComponents/CartDrawer";
import DrawerTitle from "src/Components/Drawer/DrawerTitle";
import GradientBtn from "src/Components/WebsiteComponents/GradientBtn";
import {
  categorizeTimings,
  ICustomJwtPayload,
  isProfileCompleted,
} from "src/helpers/helperFunctions";
import httpRequest from "src/helpers/httpRequest";
import { IDoctorProfile } from "src/types/GlobalInterfaces";
import { capitalize, Skeleton, Tooltip } from "@mui/material";
import TimeIcon from "src/assets/images/website-images/time.png";
import DateIcon from "src/assets/images/website-images/date.png";
import { CURRENCY } from "src/lib/constans";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import tickIcon from "src/assets/images/checked-icon-modal.png";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { faCreditCard } from "@fortawesome/free-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import EditProfileScreen from "src/Components/AllTabs/EditProfileScreen";
import { tooglePleaseLogin } from "src/Store/reducer";

const stripePromise = loadStripe(
  process.env.REACT_APP_VITE_STRIPE_PK as string
);

interface IProps {
  data: IDoctorProfile | null;
  doctorId: string;
}

interface ISlots {
  id: string;
  from: string;
  to: string;
  isBooked: boolean;
  isOnline: boolean;
  isPast: boolean;
}

interface IBooking {
  doctorId: string;
  hospitalId: string;
  slotId: string;
  appoinmentDate: string;
  patientName: string;
  phone?: string;
  isOnlineAppoinment: boolean;
  totalAmount: number;
  startTime: string;
  endTime: string;
}

const getNextAvailableDays = (numDays: number, availableDays: string[]) => {
  const result = [];
  let currentDate = moment().startOf("day");
  for (let i = 0; i < numDays; i++) {
    const dayOfWeek = currentDate.format("dddd").toLowerCase();
    const formattedDate = currentDate.format("YYYY-MM-DD");
    if (availableDays.includes(dayOfWeek)) {
      result.push({
        date: formattedDate,
      });
    }
    currentDate = currentDate.add(1, "days");
  }
  return result;
};

const LabelandIcon = ({
  label = "",
  icon,
  className,
}: {
  label?: string | null;
  icon: string;
  className?: string;
  size?: number;
}) => {
  return (
    <div className={`flex items-center gap-2 w-fit ${className}`}>
      <img src={icon} alt="" className="w-3.5 h-3.5 md:w-[18px] md:h-[18px]" />
      <p className="font-size-15px truncate font-Poppins-Regular whitespace-nowrap text-[#5B5B5B] w-[calc(100%-16px)] md:w-[calc(100%-16px)]">
        {label}
      </p>
    </div>
  );
};

const AppointmentPaymentForm: React.FC<{
  data: IBooking;
  allSlots: ISlots[];
  setSuccessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ data, allSlots, setSuccessModal, setOpenDrawer }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [payLoader, setPayLoader] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      console.error("Stripe or Elements not loaded");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);
    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      console.error("Card Element(s) not found");
      return;
    }

    setPayLoader(true);
    const { error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
    });

    if (error) {
      toast(error.message, { type: "error" });
      setPayLoader(false);
      return;
    }
    try {
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
        });

      if (paymentMethodError) {
        toast(paymentMethodError.message, { type: "error" });
        setPayLoader(false);
        return;
      }

      const paymentIntentResponse = await httpRequest<{
        clientSecret: string;
        paymentIntentId: string;
      }>({
        method: "post",
        path: "/stripe/create-payment-intent",
        params: { currency: CURRENCY, amount: data.totalAmount },
      });

      if (paymentIntentResponse.err) {
        toast(paymentIntentResponse.err, { type: "error" });
        throw new Error(paymentIntentResponse.err);
      }

      const clientSecret = paymentIntentResponse?.res?.clientSecret || "";

      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });

      if (confirmError) {
        toast(confirmError.message, { type: "error" });
        setPayLoader(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        const { res, err } = await httpRequest<any>({
          method: "post",
          path: "/appoiment/booking",
          params: data,
        });
        if (res) {
          setSuccessModal(true);
          setOpenDrawer(false);
        } else {
          toast(err, { type: "error" });
        }
      } else {
        toast("Payment failed. Please try again.", { type: "error" });
      }
    } catch (err) {
      toast("An error occurred during processing. Please try again.", {
        type: "error",
      });
    } finally {
      setPayLoader(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="box-design w-full p-3">
        <p className="font-Poppins-Medium font-size-18px text-[#2F2F31] pb-2">
          Payment Method
        </p>
        <div className="w-full rounded-lg border border-[#22C55E] mb-2 flex px-3 py-2 items-center gap-2 bg-[#F0FDF4]">
          <FontAwesomeIcon icon={faCreditCard} size="lg" />
          <p className="text-[#2F2F31] font-Poppins-Regular font-size-15px">
            Card
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <div>
            <div className="visa-card-number relative">
              <div className="card-icon-checkout">
                <FontAwesomeIcon icon={faCreditCard} size="lg" />
              </div>
              <CardNumberElement
                className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input pl-[calc(32px+0.6rem)]"
                id="cardNumber"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <CardExpiryElement
                className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input"
                id="CardExpiryElement"
              />
            </div>
            <div>
              <CardCvcElement
                className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input"
                id="CardCvcElement"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full bg-white shadow-[0px_-1px_12px_#00000029] p-3 h-[60px] flex items-center gap-3">
          <p className="font-size-18px font-Poppins-SemiBold text-[#2F2F31]">
            Fee: {CURRENCY}{" "}
            {data && data?.totalAmount
              ? (data?.totalAmount / 100).toLocaleString()
              : "0.00".toLocaleString()}
          </p>
          <div className="flex-1">
            <GradientBtn
              ButtonText="Confirm"
              loader={payLoader}
              type="submit"
              btnExtraStyle={{
                width: "100%",
              }}
            />
          </div>
        </div>
      </form>
    </>
  );
};

const selectLowestFeeHospital = (timings: any[]) => {
  if (!timings || timings.length === 0) return null;

  return timings.reduce((lowest, current) => {
    const currentFees = parseFloat(current.fees);
    const lowestFees = lowest ? parseFloat(lowest.fees) : Infinity;
    return currentFees < lowestFees ? current : lowest;
  }, null);
};

const DoctorAppointmentSide: React.FC<IProps> = ({ data, doctorId }) => {
  const { userDetails, isLogged } = useSelector(
    (state: RootState) => state.appReducer
  );
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [availableDays, setAvailableDays] = useState<
    {
      date: string;
      totalSlots: number;
      availableSlots: number;
      bookedSlots: number;
      isAvailable: boolean;
    }[]
  >([]);
  const [allSlots, setAllSlots] = useState<
    { dayTime: string; slots: ISlots[] }[]
  >([]);
  const [slotsLoader, setSlotsLoader] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<ISlots | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isBookedAppointment, setIsBookedAppointment] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false);
  const [datesLoader, setDatesLoader] = useState(false);
  const [bookSlots, setBookSlots] = useState<IBooking>({
    doctorId: "",
    hospitalId: "",
    slotId: "",
    appoinmentDate: "",
    isOnlineAppoinment: false,
    totalAmount: 0,
    patientName: "",
    phone: "",
    startTime: "",
    endTime: "",
  });
  const [disabledTabs, setDisabledTabs] = useState<{
    inPerson: boolean;
    online: boolean;
  }>({
    inPerson: false,
    online: false,
  });

  const dispatch = useDispatch();

  const fetchSlots = async (_date: string) => {
    setSlotsLoader(true);
    const { res, err } = await httpRequest<ISlots[]>({
      method: "post",
      path: "/appoiment/doctor/slots",
      params: {
        dateAvalible: moment(new Date(_date)).toISOString(),
        doctotId: doctorId,
        hospitalId: bookSlots.isOnlineAppoinment
          ? data?.availability?._id
          : selectedLocation?._id,
      },
    });
    if (res) {
      const currentMoment = moment();
      const currentDate = currentMoment.format("YYYY-MM-DD");
      const currentTime = currentMoment.format("HH:mm");

      const updatedSlots = res.map((slot) => {
        const slotDate = moment(_date).format("YYYY-MM-DD");
        const slotTime = slot.from;

        if (moment(slotDate).isBefore(currentDate)) {
          return { ...slot, isPast: true };
        }
        if (slotDate === currentDate && slotTime < currentTime) {
          return { ...slot, isPast: true };
        }
        return slot;
      });
      const { afternoonSlots, eveningSlots, morningSlots } =
        categorizeTimings(updatedSlots);
      setAllSlots([
        ...(morningSlots.length > 0
          ? [{ dayTime: "Morning", slots: morningSlots }]
          : []),
        ...(afternoonSlots.length > 0
          ? [{ dayTime: "Afternoon", slots: afternoonSlots }]
          : []),
        ...(eveningSlots.length > 0
          ? [{ dayTime: "Evening", slots: eveningSlots }]
          : []),
      ]);
      setSelectedSlot(null);
    } else {
      toast(err, { type: "error" });
    }
    setSlotsLoader(false);
  };

  useEffect(() => {
    setSelectedLocation(null);
    setSelectedSlot(null);
    setSelectedDate("");
    setAvailableDays([]);
    setAllSlots([]);

    let inPerson = false;
    let online = false;
    if (data?.timings && data?.timings?.length === 0) {
      inPerson = true;
    }
    if (!data?.availability || !data?.availability?._id || Object.keys(data?.availability).length <= 2) {
      online = true;
    }

    if(inPerson && !online) {
      setBookSlots((prev) => ({
        ...prev,
        isOnlineAppoinment: true,
      }));
    }
    setDisabledTabs({ inPerson, online });

    if (data) {
      if (bookSlots.isOnlineAppoinment && !online) {
        setBookSlots((prev) => ({
          ...prev,
          doctorId: doctorId,
          patientName:
            capitalize(userDetails?.firstName || "") +
            " " +
            capitalize(userDetails?.lastName || ""),
          phone: userDetails?.number || "",
          hospitalId: data?.availability?._id || "",
          totalAmount: parseFloat(data?.availability?.fees || "0") * 100,
        }));
        setSelectedLocation(data?.availability);
      } else {
        if (!inPerson) {
        setBookSlots((prev) => ({
          ...prev,
          doctorId: doctorId,
          patientName:
            capitalize(userDetails?.firstName || "") +
            " " +
            capitalize(userDetails?.lastName || ""),
          phone: userDetails?.number || "",
        }));

        const hospital = data?.timings
          ? selectLowestFeeHospital(data?.timings)
            : null;

          setSelectedLocation(hospital);
        }
      }
    }
  }, [data, bookSlots.isOnlineAppoinment]);

  const fetchSlotsInterval = async () => {
    setBookSlots((prev) => ({
      ...prev,
      hospitalId: selectedLocation?._id,
      totalAmount: parseFloat(selectedLocation?.fees || "0") * 100,
    }));
    let avDays = getNextAvailableDays(
      30,
      selectedLocation ? Object.keys(selectedLocation) : []
    );
    setDatesLoader(true);
    const { res, err } = await httpRequest<{
      results: {
        date: string;
        totalSlots: number;
        availableSlots: number;
        bookedSlots: number;
        isAvailable: boolean;
      }[];
    }>({
      method: "post",
      path: "/appoiment/slots-count",
      params: {
        dates: avDays.map((day) => day.date),
        doctorId: doctorId,
        hospitalId: selectedLocation?._id,
      },
    });
    if (res) {
      let slots = res?.results;
      console.log("res", slots);
      setAvailableDays(slots);
      // if (!selectedDate) {
      let date =
        slots.find((slot) => slot.availableSlots > 0)?.date || slots[0]?.date;
      setSelectedDate(date);
      if (!slotsLoader) {
        fetchSlots(date);
      }
      // } else {
      //   if (!slotsLoader) {
      //     fetchSlots(selectedDate);
      //   }
      // }
    } else {
      toast(err, { type: "error" });
    }
    setDatesLoader(false);
  };

  useEffect(() => {
    if (selectedLocation) {
      fetchSlotsInterval();
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedDate) {
      let ele = document?.querySelector(".active-day-indicator");
      ele?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      setBookSlots((prev) => ({
        ...prev,
        appoinmentDate: selectedDate,
        slotId: selectedSlot?.id || "",
        endTime: selectedSlot?.to || "",
        startTime: selectedSlot?.from || "",
      }));
      // fetchSlots(selectedDate);
    }
  }, [selectedDate]);

  let border_Class = "border-[#E6E6E6]";

  const handleDateChange = (direction: "Next" | "Previous") => {
    if (availableDays.length === 0) return;
    const currentIndex = availableDays.findIndex(
      (day) => day?.date === selectedDate
    );
    let newIndex = currentIndex;
    if (direction === "Next") {
      newIndex = (currentIndex + 1) % availableDays.length;
    } else if (direction === "Previous") {
      newIndex =
        (currentIndex - 1 + availableDays.length) % availableDays.length;
    }
    setSelectedDate(availableDays[newIndex].date);
    fetchSlots(availableDays[newIndex].date);
  };

  return (
    <>
      {successModal && (
        <div className="modal-stripe-overlay z-50">
          <div className="modal-stripe p-6">
            <div className="password-update-success">
              <img src={tickIcon} alt="" />
            </div>
            <h2 className="text-[#2F2F31] leading-tight font-size-40px font-Poppins-SemiBold text-center">
              Your appointment has been booked for
            </h2>
            <p className="font-Poppins-Regular font-size-18px text-[#474747]">
              {moment(selectedDate).format("MMM DD")},{" "}
              {moment(selectedSlot?.from, "HH:mm").format("h:mm A")}
            </p>
            <Link
              to="/"
              className="back-to-home-btn text-white font-size-18px font-Poppins-SemiBold"
            >
              Back To Home
            </Link>
          </div>
        </div>
      )}
      <CartDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {showEditProfile && !isBookedAppointment && isLogged && (
          <EditProfileScreen
            onBack={() => setShowEditProfile(false)}
            forceBack
            onSuccess={(data) => {
              console.log("userDetails", data);
              setBookSlots((prev) => ({
                ...prev,
                patientName:
                  capitalize(data?.firstName || "") +
                  " " +
                  capitalize(data?.lastName || ""),
                phone: data?.number || "",
              }));
              setShowEditProfile(false);
              setIsBookedAppointment(true);
            }}
          />
        )}
        {!showEditProfile && (
          <>
            <DrawerTitle
              title={
                isBookedAppointment ? "Confirm Booking" : "Select Facility"
              }
              onBack={() => setDrawerOpen(false)}
              backBtn
            />
            <div className="p-3 xl:p-4">
              {isBookedAppointment && isLogged && (
                <div className="flex flex-col gap-3">
                  <div className="w-full box-design">
                    <p className="font-Poppins-Medium font-size-18px text-[#2F2F31] py-2 px-3">
                      Booking Details
                    </p>
                    <h2 className="w-full flex gap-3 font-size-17px font-Poppins-Medium text-[#00443F] items-center p-3 bg-[#F1F5F9]">
                      <FontAwesomeIcon icon={faUser} size="xl" />
                      {bookSlots.isOnlineAppoinment ? "Online" : "In Person"}
                    </h2>
                    <div className="w-full p-3 flex flex-col gap-3">
                      <div className="flex gap-3 items-center">
                        <div className="min-w-[40px] min-h-[40px] bg-[#E6EEFF] rounded-md flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            className="text-[#2563EB] font-size-20px"
                          />
                        </div>
                        <p className="font-Poppins-Regular line-clamp-2 font-size-15px text-[#5B5B5B]">
                          {bookSlots.isOnlineAppoinment
                            ? "Online Appointment"
                            : selectedLocation?.hospitalName ||
                              "Unknown Hospital"}
                          {/* Shahrah e Faisal Road, Karachi, Pakistan */}
                        </p>
                      </div>
                      <h2 className="w-full font-size-18px font-Poppins-Medium text-[#00443F]">
                        Date and Time
                      </h2>
                      <div className="w-full flex gap-3 items-center">
                        <LabelandIcon
                          label={moment(selectedDate).format("DD MMM")}
                          icon={DateIcon}
                        />
                        <LabelandIcon
                          label={
                            selectedSlot
                              ? moment(selectedSlot?.from, "HH:mm").format(
                                  "hh:mm A"
                                )
                              : "--:-- --"
                          }
                          icon={TimeIcon}
                        />
                      </div>
                    </div>
                  </div>
                  <Elements stripe={stripePromise}>
                    <AppointmentPaymentForm
                      data={bookSlots}
                      allSlots={allSlots.flatMap((slot) => slot?.slots)}
                      setSuccessModal={setSuccessModal}
                      setOpenDrawer={setDrawerOpen}
                    />
                  </Elements>
                </div>
              )}
              {!isBookedAppointment && (
                <div className="flex flex-col gap-3 xl:gap-4">
                  {data?.timings && data?.timings?.length === 0 ? (
                    <p className="text-center">No available hospital.</p>
                  ) : !data ? (
                    Array.from({ length: 2 }).map((_, index) => (
                      <div
                        key={index}
                        className="p-2 flex justify-start gap-3 box-design animate-pulse bg-gray-200 rounded-md"
                      >
                        <Skeleton
                          variant="rectangular"
                          width={90}
                          height={90}
                          className="rounded-md"
                        />
                        <div className="flex-1 flex flex-col gap-1">
                          <Skeleton variant="text" width="60%" height={20} />
                          <Skeleton variant="text" width="40%" height={16} />
                        </div>
                      </div>
                    ))
                  ) : (
                    data?.timings?.map((hospitals) => (
                      <button
                        className={`p-2 flex justify-start gap-3 box-design disabled:opacity-50 disabled:cursor-not-allowed ${
                          selectedLocation?._id === hospitals._id
                            ? "border !border-[#22C55E] bg-[#F0FDF4]"
                            : ""
                        } items-center h-fit cursor-pointer transition-all`}
                        onClick={() => {
                          setSelectedLocation(hospitals);
                        }}
                        disabled={datesLoader || slotsLoader}
                      >
                        <img
                          src="https://t3.ftcdn.net/jpg/02/11/15/66/360_F_211156620_CeBr5etdTNXLb231sFcQ8M9YD1OY5IW8.jpg"
                          alt="Hospital"
                          className="max-w-[90px] h-[90px] xl:max-w-[4.3vw] xl:h-[4.3vw] w-full object-cover rounded-md"
                        />
                        <div className="w-[calc(100%-90px)] items-start xl:w-[calc(100%-4.3vw)] flex flex-col">
                          <h3 className="font-Poppins-Medium break-all line-clamp-3 text-left font-size-15px text-[#2F2F31]">
                            {hospitals.hospitalName}
                          </h3>
                          {/* <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
                            {"8.3 KM"}
                          </p> */}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CartDrawer>
      <div className="w-[30%] parent-box bg-white h-fit p-0 flex flex-col overflow-hidden">
        <div className="w-full px-3 flex-flex-col gap-3">
          <h2
            className={`border-b ${border_Class} text-center py-3 font-Poppins-SemiBold font-size-20px theme-color-green`}
          >
            Book Appointment
          </h2>
          <div className="w-full py-3 flex gap-5">
            {["In Person", "Online"].map((item) => {
              return (
                <button
                  className={`disabled:opacity-50 disabled:cursor-not-allowed ${
                    item ===
                    (bookSlots.isOnlineAppoinment ? "Online" : "In Person")
                      ? "text-white theme-gradient-green-bottom"
                      : "text-[#303030] bg-[#F2F8FD] border border-[#00443F]"
                  } flex items-center justify-center gap-1.5 font-size-15px font-Poppins-Regular py-2 min-h-[40px] w-[50%] rounded-md`}
                  onClick={() => {
                    setBookSlots((prev) => ({
                      ...prev,
                      isOnlineAppoinment: item === "Online",
                    }));
                  }}
                  disabled={datesLoader || slotsLoader || disabledTabs[item === "Online" ? "online" : "inPerson"]}
                >
                  <FontAwesomeIcon
                    icon={item === "In Person" ? faUser : faVideo}
                    className="font-size-18px"
                  />{" "}
                  {item}
                </button>
              );
            })}
          </div>
        </div>
        <div className="w-full bg-[#F2F8FD] p-3 flex flex-col gap-3">
          <p className="text-[#262626] font-size-14px font-Poppins-Regular">
            {bookSlots.isOnlineAppoinment
              ? "Your Appointment Will Be Held Online"
              : "Your Appointment Will Be Booked At This Facility"}
          </p>
          <div className="w-full flex gap-3 justify-between">
            <div className="flex-1 flex gap-3 items-center max-w-[calc(100%-130px)]">
              <div className="min-w-[40px] min-h-[40px] bg-[#E6EEFF] rounded-md flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-[#2563EB] font-size-20px"
                />
              </div>
              {!data ? (
                <Skeleton variant="text" width="80%" height={30} />
              ) : (
                <p className="font-Poppins-Regular line-clamp-2 font-size-15px text-[#5B5B5B]">
                  {bookSlots.isOnlineAppoinment
                    ? "Online Appointment"
                    : selectedLocation?.hospitalName || "Unknown Hospital"}
                </p>
              )}
            </div>
            {!bookSlots.isOnlineAppoinment ? (
              <GradientBtn
                ButtonText="Change"
                onPress={() => {
                  setIsBookedAppointment(false);
                  setDrawerOpen(true);
                }}
                fontfamilyBtn="font-Poppins-Medium"
                fontsizeBtn="font-size-14px"
                btnExtraStyle={{
                  borderRadius: 5,
                  padding: "4px 1.5rem",
                }}
                disabled={disabledTabs.inPerson}
              />
            ) : null}
          </div>
        </div>
        <div className="w-full px-3 flex flex-col gap-5 mt-5">
          <div className="w-full flex flex-col gap-2">
            <h2 className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
              Please Select Date & Time
            </h2>
            <div className="w-full flex gap-3 justify-between items-center">
              {!data || datesLoader ? (
                <Skeleton variant="text" width="30%" height={20} />
              ) : (
                <p className="font-Poppins-Medium font-size-15px text-[#2F2F31]">
                  {selectedDate
                    ? moment(selectedDate).format("MMM YYYY")
                    : "No date selected"}
                </p>
              )}
              <div className="flex gap-2">
                {["Previous", "Next"].map((direction) => (
                  <button
                    key={direction}
                    onClick={() =>
                      handleDateChange(direction as "Next" | "Previous")
                    }
                    disabled={datesLoader}
                    className="w-[20px] h-[20px] disabled:opacity-50 flex items-center justify-center rounded border border-[#00443F]"
                  >
                    <FontAwesomeIcon
                      icon={
                        direction === "Next" ? faChevronRight : faChevronLeft
                      }
                      className="text-[#00443F] font-size-13px"
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="w-full flex">
              <div className="w-fit flex gap-2 overflow-x-auto scroll-hide">
                {!data || datesLoader
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width={60}
                        height={80}
                        className="rounded-xl"
                      />
                    ))
                  : availableDays.map((day) => (
                      <button
                        key={day.date}
                        onClick={() => {
                          setSelectedDate(day.date);
                          fetchSlots(day.date);
                        }}
                        className={`${
                          day?.date === selectedDate
                            ? "theme-gradient-green-bottom border-transparent active-day-indicator"
                            : "bg-[#F2F8FD] border-[#E6E6E6]"
                        } py-1 xl:py-2 px-1.5 xl:px-2.5 relative rounded-xl border hover:shadow-sm transition-all duration-200 ease-in-out active:scale-95`}
                      >
                        <p
                          className={`font-Poppins-Regular ${
                            day?.date === selectedDate
                              ? "text-white"
                              : "text-[#5B5B5B]"
                          } font-size-13px`}
                        >
                          {moment(day?.date).format("ddd")}
                        </p>
                        <p
                          className={`font-Poppins-Medium ${
                            day?.date === selectedDate
                              ? "text-white"
                              : "text-[#2F2F31]"
                          } font-size-17px`}
                        >
                          {moment(day?.date).format("DD")}
                        </p>
                        <p
                          className={`font-Poppins-Regular absolute ${
                            day?.date === selectedDate
                              ? "text-white"
                              : "text-[#5B5B5B]"
                          } font-size-11px whitespace-nowrap`}
                        >
                          {day?.availableSlots > 0
                            ? day?.availableSlots?.toString().padStart(2, "0")
                            : "No"}{" "}
                          Slots
                        </p>
                        <p
                          className={`font-Poppins-Regular opacity-0  ${
                            day?.date === selectedDate
                              ? "text-white"
                              : "text-[#5B5B5B]"
                          } font-size-11px whitespace-nowrap`}
                        >
                          00 Slots
                        </p>
                      </button>
                    ))}
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col gap-1">
            {selectedDate && !datesLoader && (
              <>
                <h2 className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
                  {moment(selectedDate).format("dddd, MMMM DD, YYYY")}
                </h2>
                <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
                  Time Slots Are In Pakistan Standard Time (UTC +5)
                </p>
              </>
            )}
            {slotsLoader || datesLoader ? (
              <div className="w-full flex flex-col gap-2 mt-3">
                <Skeleton variant="text" width="20%" height={25} />
                <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 xl:gap-2">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width="100%"
                      height={40}
                      className="rounded-md"
                    />
                  ))}
                </div>
              </div>
            ) : allSlots.length === 0 ? (
              <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
                No time slots available
              </p>
            ) : (
              allSlots.map((dayTimeSlots) => (
                <div className="w-full flex flex-col gap-2 mt-3">
                  <h3 className="font-Poppins-Medium lowercase first-letter:capitalize font-size-15px text-[#2F2F31]">
                    {dayTimeSlots.dayTime}
                  </h3>
                  <div className="w-full grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-1.5 xl:gap-2.5">
                    {dayTimeSlots.slots.map((slot) => (
                      <Tooltip
                        title={
                          slot?.isPast
                            ? "Time Passed"
                            : slot.isBooked
                            ? "Already Booked"
                            : "Book Appointment at " +
                              moment(slot.from, "HH:mm").format("hh:mm A")
                        }
                      >
                        <button
                          key={slot.id}
                          className={`border 2xl:p-2  lg:p-1.5 p-1 rounded-md ${
                            slot.isBooked || slot.isPast
                              ? "opacity-40 cursor-default"
                              : "cursor-pointer hover:shadow-sm transition-all duration-200 ease-in-out active:scale-95"
                          } ${
                            selectedSlot?.id === slot.id
                              ? "theme-gradient-green-bottom border-transparent text-white"
                              : "bg-[#F2F8FD] border-[#00443F] text-[#00443F]"
                          }`}
                          onClick={() => {
                            if (!slot.isBooked && !slot.isPast) {
                              setSelectedSlot(slot);
                              setBookSlots((prev) => ({
                                ...prev,
                                slotId: slot.id,
                                endTime: slot?.to,
                                startTime: slot?.from,
                              }));
                            }
                          }}
                        >
                          <p className="font-Poppins-Regular whitespace-nowrap font-size-16px text-center">
                            {moment(slot.from, "HH:mm").format("hh:mm A")}
                          </p>
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="w-full px-3 py-2 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] flex gap-3 mt-5" 
          style={{
            flexDirection: isLogged && !isProfileCompleted(userDetails as ICustomJwtPayload) ? "column" : "row",
          }}
        >
          {!slotsLoader && selectedDate && selectedSlot && (
            <div className="flex flex-col">
              <p className="font-Poppins-Regular whitespace-nowrap text-[#5B5B5B] font-size-13px">
                {moment(selectedDate).format("dddd")}
              </p>
              <p className="font-Poppins-Medium whitespace-nowrap text-[#2F2F31] font-size-17px">
                {selectedSlot
                  ? moment(selectedSlot?.from, "HH:mm").format("hh:mm A")
                  : "--:-- --"}
                , {moment(selectedDate).format("DD MMM")}
              </p>
              <p className="font-Poppins-Regular whitespace-nowrap text-[#5B5B5B] font-size-11px mt-[-2px]">
                {bookSlots.isOnlineAppoinment ? "Online" : "In Person"}
              </p>
              {((bookSlots.isOnlineAppoinment && data?.availability) ||
                (selectedLocation && !bookSlots.isOnlineAppoinment)) && (
                <p className="font-Poppins-SemiBold whitespace-nowrap text-[#2F2F31] font-size-19px">
                  {CURRENCY}{" "}
                  {bookSlots.isOnlineAppoinment
                    ? parseFloat(
                        data?.availability?.fees || "0"
                      ).toLocaleString()
                    : selectedLocation?.fees
                    ? parseFloat(selectedLocation?.fees || "0").toLocaleString()
                    : "0.00".toLocaleString()}
                </p>
              )}
            </div>
          )}
          <div className="flex-1 flex items-center justify-center">
            <GradientBtn
              ButtonText={
                isLogged
                  ? isProfileCompleted(userDetails as ICustomJwtPayload)
                    ? "Continue"
                    : "Complete Profile to Continue"
                  : "Continue"
              }
              onPress={() => {
                if (!isLogged) {
                  dispatch(tooglePleaseLogin(true));
                  return;
                }
                if (!isLogged || !selectedSlot) return;
                if (isProfileCompleted(userDetails as ICustomJwtPayload)) {
                  setDrawerOpen(true);
                  setIsBookedAppointment(true);
                } else {
                  setDrawerOpen(true);
                  setShowEditProfile(true);
                }
              }}
              btnExtraStyle={{
                width: "100%",
                transition: "all 0.3s",
                opacity: selectedSlot ? 1 : 0.5,
                cursor: selectedSlot ? "pointer" : "not-allowed",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DoctorAppointmentSide;
